/**
 * 招待の受け取りの画面（W22・FR16）。**認証の外側**に置く。
 *
 * 招待された人はまだこの会社の利用者ではないので、認証済みの区画に入れると
 * ログイン画面へ飛ばされてしまう（代行の受け取り口と同じ理由）。
 *
 * ## トークンは URL のフラグメントで受ける
 *
 * `#token=…` はサーバーへ送られないので、アクセスログにも Referer にも
 * 残らない。クエリに置くと、リンクを踏んだ時点で中継のログに平文が残る。
 * 読み取ったらすぐに履歴から消す（戻るボタンで再表示されないようにする）。
 *
 * ## 失敗の文言は 1 種類
 *
 * 口の側が「見つからない・期限切れ・取り消し済み・受け取り済み」を同じ
 * 応答にしているので、画面でも**理由を書き分けない**。書き分けると、
 * 画面が口より多くを知っていることになり、そこから列挙できてしまう。
 * 入力そのものの不備だけは、口が別の本文で返すのでそのまま出す。
 */

import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  Button,
  invitationAcceptEndpoint,
  useSession,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

/** 口が決めている最低の長さ（`admin-invitations.ts` と同じ値）。 */
const MIN_PASSWORD_LENGTH = 12;

/** どの理由でも同じ 1 行。口の応答と同じく書き分けない。 */
const INVALID_MESSAGE =
  "この招待は使えません。期限が切れているか、取り消されたか、すでに受け取られています。招待した方にご連絡ください。";

/** URL のフラグメントからトークンを読む。読んだらフラグメントを消す。 */
function takeTokenFromHash(): string {
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const token = new URLSearchParams(hash).get("token") ?? "";
  if (token !== "") {
    // 戻るボタンや共有で再表示されないよう、履歴から消す
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  }
  return token;
}

export function InvitationAcceptPage() {
  const session = useSession();
  const [token, setToken] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setToken(takeTokenFromHash());
  }, []);

  const endpoint = invitationAcceptEndpoint(session.config.graphqlEndpoint);

  async function submit(): Promise<void> {
    if (endpoint === null) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, displayName, password }),
      });
      if (response.ok) {
        setDone(true);
        return;
      }
      const body = (await response.json().catch(() => null)) as {
        error?: string;
        error_description?: string;
      } | null;
      setError(
        body?.error === "invalid_input" && body.error_description !== undefined
          ? body.error_description
          : INVALID_MESSAGE,
      );
    } catch {
      // 通信そのものが失敗した。招待の当否は分からないので、そう書く
      setError(
        "通信に失敗しました。しばらく待ってから、もう一度お試しください。",
      );
    } finally {
      setBusy(false);
    }
  }

  if (endpoint === null) {
    return (
      <div className="vd-login">
        <div className="vd-login-card">
          <h1>招待を受け取れません</h1>
          <p>この環境では招待の受け取りが有効になっていません。</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="vd-login">
        <div className="vd-login-card">
          <h1>登録が済みました</h1>
          <p>
            ログインしてください。すでにアカウントをお持ちだった場合、パスワードは変わっていません。
          </p>
          <Link to="/login">ログインへ</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="vd-login">
      <div className="vd-login-card">
        <div className="vd-row" style={{ justifyContent: "space-between" }}>
          <h1>visumo 管理画面への招待</h1>
          <HelpIcon slug="account/invitation" />
        </div>
        <p>お名前とパスワードを決めると、管理画面を使えるようになります。</p>

        {error === null ? null : (
          <div className="vd-state vd-state--error" role="alert">
            <span>{error}</span>
          </div>
        )}

        {token === "" ? (
          <p>
            招待のリンクが正しくありません。メールのリンクをそのまま開いてください。
          </p>
        ) : (
          <div className="vd-stack">
            <label htmlFor="invitation-display-name">お名前</label>
            <input
              id="invitation-display-name"
              type="text"
              autoComplete="name"
              value={displayName}
              disabled={busy}
              onChange={(event) => {
                setDisplayName(event.target.value);
              }}
            />
            <label htmlFor="invitation-password">
              {`パスワード（${String(MIN_PASSWORD_LENGTH)} 文字以上）`}
            </label>
            <input
              id="invitation-password"
              type="password"
              autoComplete="new-password"
              value={password}
              disabled={busy}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
            <span>
              <Button
                variant="primary"
                disabled={
                  busy ||
                  displayName.trim() === "" ||
                  password.length < MIN_PASSWORD_LENGTH
                }
                onClick={() => {
                  void submit();
                }}
              >
                登録する
              </Button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
